import React from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Check, Lock } from 'lucide-react';
import { StepCircle } from './StepCircle';

interface StepHeaderProps {
  stepNumber: number;
  title: string;
  description: string;
  isCompleted?: boolean;
  sectionId?: string;
  onComplete?: () => void;
  marginTop?: 4 | 5;
  disabled?: boolean;
  disabledReason?: string;
}

export const StepHeader: React.FC<StepHeaderProps> = ({
  stepNumber,
  title,
  description,
  isCompleted = false,
  sectionId,
  onComplete,
  marginTop = 4,
  disabled = false,
  disabledReason,
}) => {
  const renderButton = () => {
    const button = (
      <Button
        variant={disabled ? 'outline-secondary' : 'outline-success'}
        size="sm"
        onClick={onComplete}
        disabled={disabled}
        className="d-flex align-items-center justify-content-center gap-1 px-2 py-1"
        style={{ minWidth: '130px' }}
      >
        {disabled ? (
          <>
            <Lock size={14} />
            Click to Complete
          </>
        ) : (
          <>
            <Check size={14} />
            Click to Complete
          </>
        )}
      </Button>
    );

    if (disabled && disabledReason) {
      return (
        <OverlayTrigger
          placement="left"
          overlay={
            <Tooltip id={`tooltip-step-${stepNumber}`}>
              <div className="text-start">
                <strong>Requirements:</strong>
                <br />
                {disabledReason}
              </div>
            </Tooltip>
          }
        >
          <span>{button}</span>
        </OverlayTrigger>
      );
    }

    return button;
  };

  return (
    <div id={sectionId} className={`mb-3 mt-${marginTop}`}>
      <div className="d-flex align-items-center gap-3 p-4 rounded-3 border">
        <StepCircle stepNumber={stepNumber} status={isCompleted ? 'completed' : 'pending'} size={48} />
        <div className="flex-grow-1">
          <h4 className="mb-1 fw-bold text-dark">{title}</h4>
          <small className="text-muted">{description}</small>
        </div>
        <div className="d-flex align-items-center gap-2">
          {isCompleted ? (
            <Button
              variant="success"
              size="sm"
              disabled
              className="d-flex align-items-center justify-content-center gap-1 px-2 py-1"
              style={{ minWidth: '130px', opacity: 1 }}
            >
              <Check size={14} />
              Completed
            </Button>
          ) : (
            onComplete && renderButton()
          )}
        </div>
      </div>
    </div>
  );
};
